import { BeatResolution, Key, MIDDLE_C, TICKS_PER_LOOP } from '@arpeggi/shared'
import { toJS } from 'mobx'

interface EventMapTrigger {
  type: 'noteOn' | 'noteOff'
  rate: number
  tickTime: number
}

/**
 * Gets the semitone offset for this row and sample.
 * e.g. If row is 64 (middle C) and sample's key is D (2), this needs to return -2,
 * so that the sample will get played at the correct pitch.
 */
function getSemitoneOffset(pianoRollRow: number, key: Key): number {
  return pianoRollRow - (MIDDLE_C + key)
}

/**
 * Contains utilities for converting MobX Stores into Elementary Event Maps.
 * Note: don't import the MobX Stores directly, since this code will be used by the server.
 */

export function createInstrumentEventMap(stem): EventMapTrigger[] {
  if (stem.loops.length === 0) return []
  const loops = toJS(stem.loops)
  const arrangement: number[] = toJS(stem.stemSettings.arrangement)
  const events: EventMapTrigger[] = []
  arrangement.map((loopIndex, i) => {
    // No events for an empty section
    if (loopIndex === 0) return []
    const melodicLoop = loops[loopIndex - 1].melodicLoop
    return melodicLoop.notes.notes.map((note) => {
      // we need to leave this in as not all samples are in C, so we need to calculate the offset
      const semitone = getSemitoneOffset(note.note, stem.melodicSample.key)
      const noteDurationTicks = note.duration
      const tickTime: number = i * TICKS_PER_LOOP + note.start
      const rate = calculatePlaybackRate(semitone)
      // Currently having an issue with notes that have a tickTime of 0 -> just change these to 1
      // TODO: find a less hacky solution
      const noteOn: EventMapTrigger = {
        type: 'noteOn',
        rate,
        tickTime: tickTime === 0 ? 1 : tickTime
      }
      const noteOff: EventMapTrigger = {
        type: 'noteOff',
        rate,
        tickTime: tickTime + noteDurationTicks
      }
      events.push(...[noteOn, noteOff])
    })
  })

  return [...events]
}

export function createDrumEventMap(stem): EventMapTrigger[][] {
  const loops = toJS(stem.loops)
  if (!stem.stemSettings) return []
  const arrangement: number[] = toJS(stem.stemSettings.arrangement)

  const events = arrangement.map((loopIndex, i) => {
    // No events for an empty section
    if (loopIndex === 0) return []

    // Each loop of the drum arrangement has subloops for each sample in the drum kit
    const subLoops = loops[loopIndex - 1].drumLoop.subLoops

    return subLoops.map((subLoop) => {
      // Each subLoop has a series of notes that are either on or off
      return subLoop.notes.notes.reduce((acc, note) => {
        const tickTime: number = i * TICKS_PER_LOOP + note.start
        // Currently having an issue with notes that have a tickTime of 0 -> just change these to 1
        // TODO: find a less hacky solution
        const noteOn = { value: 1, tickTime: tickTime === 0 ? 1 : tickTime }
        // Hacky fix, but subtracting 1 prevent overlapping notes, which will not play since drums only has 1 voice
        // this doesn't really matter right now since drums are always `trigger` and duration doesn't effect that
        // TODO: make this not a hacky fix
        const noteOff = { value: 0, tickTime: tickTime + note.duration - 1 }
        acc.push(noteOn)
        acc.push(noteOff)

        return acc
      }, [])
    })
  })

  // Now we unroll the events section by section into aggregate arrays
  // for each sample in the drum kit
  const unrolled = events.reduce(function (acc, section) {
    if (section.length === 0) return acc

    return section.map(function (drum, j) {
      if (j < acc.length && Array.isArray(acc[j])) {
        return [...acc[j], ...drum]
      }

      return drum
    })
  }, [])

  return unrolled
}

export function beatCoefficient(loopBeatDivision: number): number {
  switch (loopBeatDivision) {
    case BeatResolution.QUARTER_NOTE:
      return 2
    case BeatResolution.EIGHTH_NOTE:
      return 1
    case BeatResolution.SIXTEENTH_NOTE:
      return 0.5
    case BeatResolution.EIGHTH_NOTE_TRIPLETS:
      return 2 / 3
    case BeatResolution.SIXTEENTH_NOTE_TRIPLETS:
      return 1 / 3
    default:
      throw new Error("We don't have a coefficient for that resolution.")
  }
}

export function calculatePlaybackRate(semitone: number) {
  return Math.pow(2, semitone / 12)
}

type NoteOnAndOff = {
  noteOn: any
  noteOff: any
}

interface Voice {
  semitone: number | null
  gate: Array<{ value: number; tickTime: number }>
  playbackRate: Array<{ value: number; tickTime: number }>
  key: string
}

// Allocates voices for an entire track -- across multiple loops.
// We sort all notes by endTime, so we can reduce the chance of stealing from a voice
// that is currently playing.
export function allocateVoices(key, numVoices, events) {
  const voices: Voice[] = Array.from({ length: numVoices }).map(function (_, i) {
    return {
      semitone: null,
      gate: [],
      playbackRate: [],
      key: `${key}:v${i}`
    }
  })

  // assumes that events[i] is a noteOn, and events[i+1] is its corresponding noteOff event
  let nextVoice = 0

  const noteOnAndOffs = new Array<NoteOnAndOff>()
  for (let i = 0; i < events.length; i += 2) {
    noteOnAndOffs.push({
      noteOn: events[i],
      noteOff: events[i + 1]
    })
  }
  noteOnAndOffs.sort(function (a, b) {
    return a.noteOff.tickTime - b.noteOff.tickTime
  })

  // Now we have a nice sorted array of intervals.
  // We recycle voices in order of note off, which should make voice stealing very unlikely.
  noteOnAndOffs.forEach((noteOnAndOff) => {
    const noteOn = noteOnAndOff.noteOn
    if (noteOn && noteOn.hasOwnProperty('type') && noteOn.type === 'noteOn') {
      voices[nextVoice].gate.push({
        value: 1,
        tickTime: noteOn.tickTime
      })
      voices[nextVoice].playbackRate.push({ value: noteOn.rate, tickTime: noteOn.tickTime })
      voices[nextVoice].semitone = noteOn.rate
    }

    const noteOff = noteOnAndOff.noteOff
    if (noteOff && noteOff.hasOwnProperty('type') && noteOff.type === 'noteOff') {
      if (voices[nextVoice].semitone === noteOff.rate) {
        voices[nextVoice].gate.push({ value: 0, tickTime: noteOff.tickTime })
      }
    }

    if (++nextVoice >= voices.length) nextVoice -= voices.length
  })
  return voices
}
